import { Button, CircularProgress } from "@mui/material";
import { useGoogleLogin } from "@react-oauth/google";
import { useState, useRef, useEffect } from "react";
import { apiClient } from "../utils/apiClient";
import {
  getUrlWithoutParams,
  useQueryParam,
  useRemoveAllUrlParams,
} from "../utils/url";
import GoogleIcon from "@mui/icons-material/Google";
import { SSOLoginType } from "./UserLoginPage";
import { parseStateParameter, createStateParameter } from "./ssoUtils";
import {
  CredentialsLoginWithSSOResponse,
  CredentialsLoginWithSSOBody,
} from "@services/credentials/types";

export const GoogleLoginButton = ({
  onLoginStart,
  onSuccess,
  onFailure,
  code,
  redirectUrl,
  clientNonce,
  connectNewInbox, // Don't set the auth cookies on successful login
}: {
  onLoginStart: (type: SSOLoginType) => void;
  onSuccess: (state: string) => void;
  onFailure: (reason: string) => void;
  code: string | null;
  redirectUrl: string | null;
  clientNonce?: string;
  connectNewInbox: boolean;
}) => {
  const removeAllUrlParams = useRemoveAllUrlParams();
  const stateQueryUrl = useQueryParam("state")!;
  const googleScopesParam = useQueryParam("google_scopes");
  const [loading, setLoading] = useState(false);
  const requestedSSOWithCode = useRef(false);

  const handleLogin = async (codeResponse: string) => {
    setLoading(true);
    try {
      const response = await apiClient.post<CredentialsLoginWithSSOResponse>(
        "credentials/login_with_sso",
        {
          type: "google",
          code: codeResponse,
          redirecturi: getUrlWithoutParams(),
          connectNewInbox,
          clientNonce,
        } satisfies CredentialsLoginWithSSOBody
      );

      const { status, message } = response;
      if (status === "ok") {
        const redirectUrlFromState = parseStateParameter(stateQueryUrl);
        onSuccess(redirectUrlFromState || "");
      } else {
        onFailure(message || "Login failed");
      }
    } catch (error: any) {
      console.error("Login failed:", error.message || error);
      onFailure("An error occurred while attempting to log in. Try Again");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (code && !requestedSSOWithCode.current) {
      handleLogin(code).then(() => removeAllUrlParams());
      requestedSSOWithCode.current = true;
    }
  }, [code]);

  // Build scope string with custom scopes from URL parameter
  const buildScopes = () => {
    const defaultScopes = [
      "openid",
      "email",
      "profile",
      "https://www.googleapis.com/auth/gmail.modify",
      "https://www.googleapis.com/auth/drive",
    ];

    if (googleScopesParam) {
      // Parse comma-separated scope names and convert to full Google API URLs
      const customScopes = googleScopesParam
        .split(",")
        .map((scope) => scope.trim())
        .filter((scope) => scope.length > 0)
        .map((scope) => {
          // If scope doesn't start with https://, assume it's a short name and prepend Google API URL
          if (!scope.startsWith("https://")) {
            return `https://www.googleapis.com/auth/${scope}`;
          }
          return scope;
        });

      // Combine default and custom scopes, removing duplicates
      const allScopes = [...defaultScopes, ...customScopes];
      console.log("Using Google scopes:", allScopes);
      return Array.from(new Set(allScopes)).join(" ");
    }

    return defaultScopes.join(" ");
  };

  // navigate to the google page and redirect back with a code= url param
  const login = useGoogleLogin({
    flow: "auth-code",
    ux_mode: "redirect",
    state: createStateParameter(redirectUrl, clientNonce, "google"),
    scope: buildScopes(),
    onError: (error) => {
      console.error("Google login failed:", error);
      onFailure("Google login failed. Try again.");
    },
  });

  const initiateGoogleLogin = () => {
    onLoginStart("google");
    login();
  };

  return (
    <Button
      variant="contained"
      color="primary"
      onClick={initiateGoogleLogin}
      disabled={loading}
      startIcon={<GoogleIcon />}
      fullWidth
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "10px",
        borderRadius: "5px",
        border: "1px solid #ddd",
        cursor: "pointer",
        width: "100%",
      }}
    >
      <>{loading ? <CircularProgress size={24} /> : "Continue with Google"}</>
    </Button>
  );
};
